import { useState, type ReactNode } from 'react';

import { cn } from '../cn';
import Button from '../Input2/Button';
import Input from '../Input2/Input';
import Tooltip from '../Input2/Tooltip';
import { Editor, General } from '../StrokeIcons';

export type CompactMetadataBarProps = {
  probability: number | null;
  age: number | null;
  gender: 'M' | 'F' | '?';
  title: string;

  probabilityTooltip: ReactNode;
  ageTooltip: ReactNode;

  hasAgeOverride: boolean;
  onAgeOverride: (minAge: number | null) => Promise<void>;
  isAgeOverridePending: boolean;

  onResetGender: () => Promise<void>;
  isResetGenderPending: boolean;
};

export function CompactMetadataBar({
  probability,
  age,
  gender,
  title,
  probabilityTooltip,
  ageTooltip,
  hasAgeOverride,
  onAgeOverride,
  isAgeOverridePending,
  onResetGender,
  isResetGenderPending,
}: CompactMetadataBarProps) {
  const [isEditingAge, setIsEditingAge] = useState(false);
  const [overrideValue, setOverrideValue] = useState<string>('');

  const probabilityColor =
    probability == null
      ? 'text-gray-400'
      : probability >= 70
        ? 'text-green-600 dark:text-green-400'
        : probability >= 40
          ? 'text-yellow-600 dark:text-yellow-400'
          : 'text-red-600 dark:text-red-400';

  const handleSaveAge = async () => {
    const val = overrideValue ? parseInt(overrideValue, 10) : null;
    const validAge = val != null && val >= 16 && val <= 120 ? val : null;
    await onAgeOverride(validAge);
    setIsEditingAge(false);
  };

  const handleResetAge = async () => {
    await onAgeOverride(null);
    setIsEditingAge(false);
  };

  const hasGender = gender !== '?';

  return (
    <div className="border-b border-gray-100 px-4 py-2.5 dark:border-gray-800">
      {isEditingAge && (
        <div className="mb-2.5 flex items-center gap-2">
          <Input
            type="number"
            size="xs"
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
            placeholder="Age"
            className="w-20"
            min={16}
            max={120}
          />
          <Button color="blue" size="xs" onClick={handleSaveAge} disabled={isAgeOverridePending}>
            Save
          </Button>
          <Button size="xs" text onClick={() => setIsEditingAge(false)}>
            Cancel
          </Button>
          {hasAgeOverride && (
            <Button size="xs" color="red" type="reset" onClick={handleResetAge} disabled={isAgeOverridePending}>
              Reset
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-4 gap-x-3 gap-y-1.5">
        <Tooltip tooltip={probabilityTooltip} className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Active</span>
          <div className="flex h-5 items-center gap-1">
            <span className={cn('text-base font-semibold', probabilityColor)}>
              {probability != null ? `${probability}%` : '—'}
            </span>
          </div>
        </Tooltip>

        <Tooltip tooltip={ageTooltip} className="group/age flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Age</span>
          <div className="flex h-5 items-center gap-1">
            <span className="text-base font-semibold text-gray-700 dark:text-gray-300">
              {age != null ? `${age}+` : '—'}
            </span>
            {!isEditingAge && (
              <Button
                text
                size="2xs"
                noPadding
                icon={<Editor.Pencil03 className="size-2.5 text-gray-400" />}
                tooltip="Override age"
                className="opacity-0 transition-opacity group-hover/age:opacity-60 hover:opacity-100!"
                onClick={() => {
                  setOverrideValue(age?.toString() ?? '');
                  setIsEditingAge(true);
                }}
              />
            )}
            {!isEditingAge && hasAgeOverride && (
              <Button
                text
                size="2xs"
                noPadding
                icon={<General.XMark className="size-2.5 text-gray-400" />}
                tooltip="Clear age override"
                disabled={isAgeOverridePending}
                className="opacity-0 transition-opacity group-hover/age:opacity-60 hover:opacity-100! disabled:opacity-30"
                onClick={handleResetAge}
              />
            )}
          </div>
        </Tooltip>

        <Tooltip
          tooltip={hasGender ? 'Click X to reset gender and title' : 'Research from LinkedIn to detect'}
          className="group/gender flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Gender</span>
          <div className="flex h-5 items-center gap-1">
            <span className="text-base font-semibold text-gray-700 dark:text-gray-300">{gender}</span>
            {hasGender && (
              <Button
                text
                size="2xs"
                noPadding
                icon={<General.XMark className="size-2.5 text-gray-400" />}
                tooltip="Clear gender"
                disabled={isResetGenderPending}
                className="opacity-0 transition-opacity group-hover/gender:opacity-60 hover:opacity-100! disabled:opacity-30"
                onClick={onResetGender}
              />
            )}
          </div>
        </Tooltip>

        <div className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Title</span>
          <div className="flex h-5 items-center gap-1">
            <span className="text-base font-semibold text-gray-700 dark:text-gray-300">{title}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
